import { Box, Button, Card, Heading, HStack, SimpleGrid, Text, VStack } from '@chakra-ui/react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowRight, Globe, Shield, TrendingUp, Zap } from 'lucide-react'
import FadeInSection from '@/components/business-value/FadeInSection'
import IcpOverviewCard from '@/components/business-value/IcpOverviewCard'
import MetricCard from '@/components/business-value/MetricCard'
import { allIcps } from '@/data/business-value'
import { PageHeader, SectionHeading } from '~/components/shell'

export const Route = createFileRoute('/_app/business-value/')({
  component: BusinessValueLanding,
})

const crossIndustryMetrics = [
  {
    value: 6000,
    suffix: '+',
    label: 'Customers Worldwide',
    description: 'Enterprises trust HiveMQ for mission-critical IoT',
  },
  {
    value: 175,
    suffix: '+',
    label: 'Countries',
    description: 'Global deployments spanning every continent',
  },
  {
    value: 400,
    suffix: 'M+',
    prefix: '$',
    label: 'Customer Savings',
    description: 'Documented cost savings across industries',
  },
  {
    value: 99.99,
    suffix: '%',
    label: 'Platform Uptime',
    description: 'Enterprise-grade reliability and availability',
  },
]

const whyHivemqFeatures = [
  {
    icon: Shield,
    title: 'Enterprise Reliability',
    description: 'Zero message loss with QoS guarantees, disk persistence, and automatic failover.',
  },
  {
    icon: TrendingUp,
    title: 'Infinite Scale',
    description:
      'From thousands to billions of messages  -  elastic clustering grows with your needs.',
  },
  {
    icon: Zap,
    title: 'Real-Time Performance',
    description: 'Sub-millisecond latency for time-critical industrial and IoT applications.',
  },
]

function BusinessValueLanding() {
  const navigate = useNavigate()

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader title="Business Value" subtitle="Proven ROI Across Industries" />

      <VStack align="stretch" gap={10}>
        {/* Intro copy */}
        <FadeInSection>
          <Text fontSize="md" color="content.secondary" maxW="2xl">
            Discover how leading enterprises use HiveMQ to transform operations, reduce costs, and
            unlock new revenue streams with real-time IoT data connectivity.
          </Text>
        </FadeInSection>

        {/* Stats Row */}
        <FadeInSection delay={0.1}>
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap={4}>
            {crossIndustryMetrics.map((metric, i) => (
              <MetricCard key={metric.label} metric={metric} delay={0.2 + 0.15 * i} />
            ))}
          </SimpleGrid>
        </FadeInSection>

        {/* ICP Grid */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            title="Industry Solutions"
            description="Explore how HiveMQ delivers measurable business value across key industries."
          />
          <FadeInSection delay={0.1}>
            <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
              {allIcps.map((icp, i) => (
                <FadeInSection key={icp.id} delay={0.1 * i}>
                  <IcpOverviewCard icp={icp} />
                </FadeInSection>
              ))}
            </SimpleGrid>
          </FadeInSection>
        </VStack>

        {/* Why HiveMQ */}
        <VStack align="stretch" gap={4}>
          <SectionHeading title="Why HiveMQ" />
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
            <VStack gap={5} align="start">
              {whyHivemqFeatures.map((feature, i) => {
                const Icon = feature.icon
                return (
                  <FadeInSection key={feature.title} delay={0.1 * i}>
                    <HStack gap={4} align="start">
                      <Box
                        p={2}
                        borderRadius="lg"
                        bg="brand.solid/10"
                        color="brand.solid"
                        flexShrink={0}
                      >
                        <Icon size={20} />
                      </Box>
                      <VStack gap={1} align="start">
                        <Text fontWeight="bold">{feature.title}</Text>
                        <Text fontSize="sm" color="content.secondary">
                          {feature.description}
                        </Text>
                      </VStack>
                    </HStack>
                  </FadeInSection>
                )
              })}
            </VStack>

            <FadeInSection delay={0.2} direction="right">
              <Card.Root bg="brand.solid" color="white" overflow="hidden" h="100%">
                <Card.Body p={6}>
                  <VStack gap={4} align="start">
                    <Globe size={32} />
                    <Heading size="md">Trusted by Industry Leaders</Heading>
                    <Text fontSize="sm" opacity={0.9}>
                      From automotive giants like BMW and Daimler to energy innovators like NextEra,
                      the world's most demanding enterprises rely on HiveMQ for their
                      mission-critical IoT infrastructure.
                    </Text>
                    <HStack gap={4} pt={2}>
                      <VStack gap={0} align="start">
                        <Text fontSize="2xl" fontWeight="bold">
                          6,000+
                        </Text>
                        <Text fontSize="xs" opacity={0.8}>
                          Customers
                        </Text>
                      </VStack>
                      <VStack gap={0} align="start">
                        <Text fontSize="2xl" fontWeight="bold">
                          175+
                        </Text>
                        <Text fontSize="xs" opacity={0.8}>
                          Countries
                        </Text>
                      </VStack>
                    </HStack>
                  </VStack>
                </Card.Body>
              </Card.Root>
            </FadeInSection>
          </SimpleGrid>
        </VStack>

        {/* CTA */}
        <FadeInSection delay={0.1}>
          <Card.Root overflow="hidden">
            <Card.Body p={8}>
              <VStack gap={4} align="center" textAlign="center">
                <Heading size="lg">Ready to See It in Action?</Heading>
                <Text color="content.secondary" maxW="lg">
                  Explore our interactive industry demos to see how HiveMQ transforms real-world
                  operations with live data, real-time analytics, and enterprise-grade reliability.
                </Text>
                <Button
                  size="lg"
                  onClick={() => navigate({ to: '/business-value/smart-manufacturing' })}
                  mt={2}
                >
                  <HStack gap={2}>
                    <span>Start with Smart Manufacturing</span>
                    <ArrowRight size={18} />
                  </HStack>
                </Button>
              </VStack>
            </Card.Body>
          </Card.Root>
        </FadeInSection>
      </VStack>
    </Box>
  )
}
